import { useEffect, useState, useCallback } from "react";
import { listInquiries } from "../eventsApi";
import { eventSchedule, type Inquiry } from "../types";

export function CalendarView() {
  const [events, setEvents] = useState<Inquiry[]>([]);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const fetchEvents = useCallback(async () => {
    try {
      const confirmed = await listInquiries({ status: "confirmed", limit: 100 });
      const deposited = await listInquiries({ status: "deposit_paid", limit: 100 });
      setEvents([...confirmed.data, ...deposited.data]);
    } catch (err) {
      console.error("Failed to load calendar events", err);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const daysInMonth = new Date(month.year, month.month + 1, 0).getDate();
  const firstDow = new Date(month.year, month.month, 1).getDay();
  const monthName = new Date(month.year, month.month).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const getEventsForDay = (day: number) => {
    const dateStr = `${month.year}-${String(month.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    // A Reservation has one date; before it, any preferred date shows.
    return events.filter((e) => {
      const s = eventSchedule(e);
      return s.fromReservation ? s.date === dateStr : e.preferred_dates.some((d) => d.date === dateStr);
    });
  };

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={() =>
            setMonth((m) => {
              const d = new Date(m.year, m.month - 1);
              return { year: d.getFullYear(), month: d.getMonth() };
            })
          }
          className="rounded-md px-3 py-1 text-sm text-tribal-muted hover:text-white"
        >
          ← Prev
        </button>
        <h2 className="text-lg font-semibold text-white">{monthName}</h2>
        <button
          type="button"
          onClick={() =>
            setMonth((m) => {
              const d = new Date(m.year, m.month + 1);
              return { year: d.getFullYear(), month: d.getMonth() };
            })
          }
          className="rounded-md px-3 py-1 text-sm text-tribal-muted hover:text-white"
        >
          Next →
        </button>
      </div>

      <div className="grid grid-cols-7 gap-px rounded-lg border border-tribal-border bg-tribal-border overflow-hidden">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div
            key={d}
            className="bg-tribal-surface px-2 py-1 text-center text-xs font-medium text-tribal-muted"
          >
            {d}
          </div>
        ))}
        {Array.from({ length: firstDow }).map((_, i) => (
          <div key={`empty-${i}`} className="bg-tribal-bg p-2" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dayEvents = getEventsForDay(day);
          return (
            <div key={day} className="min-h-[80px] bg-tribal-bg p-1">
              <span className="text-xs text-tribal-muted">{day}</span>
              {dayEvents.map((ev) => (
                <div
                  key={ev.id}
                  className={`mt-0.5 truncate rounded px-1 py-0.5 text-xs text-white ${
                    ev.status === "confirmed" ? "bg-green-600/80" : "bg-orange-500/80"
                  }`}
                  title={`${ev.first_name} ${ev.last_name} — ${ev.group_size} guests`}
                >
                  {ev.first_name} ({ev.group_size})
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
